import { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useUserStore } from '@/lib/userStore';
import type { Role } from '@/features/roles/roles';
import type { AccountInfo } from '@azure/msal-browser';
import { api } from '@/interceptors/axiosInstance.ts';

export function useMsalUserSync() {
  const { instance, accounts, inProgress } = useMsal();
  const setUser = useUserStore((s) => s.setUser);
  const setStatus = useUserStore((s) => s.setStatus);
  const clearUser = useUserStore((s) => s.clearUser);
  const setNeedsInteraction = useUserStore((s) => s.setNeedsInteraction);

  const buildUser = (acc: AccountInfo, roles: Role[]) => {
    const claims = acc.idTokenClaims as Record<string, unknown> | undefined;
    const firstName = (claims?.['given_name'] as string) || '';
    const lastName = (claims?.['family_name'] as string) || '';

    return {
      id: acc.localAccountId,
      email: acc.username,
      firstName,
      lastName,
      roles,
    };
  };

  const account = instance.getActiveAccount() || accounts[0] || null;
  const accountId = account?.homeAccountId;

  useEffect(() => {
    if (inProgress !== 'none') {
      return;
    }

    if (!account) {
      clearUser();
      return;
    }

    if (!instance.getActiveAccount()) {
      instance.setActiveAccount(account);
    }

    const currentStatus = useUserStore.getState().status;

    if (currentStatus === 'idle' || currentStatus === 'unauthenticated') {
      setStatus('loading');
    }

    let isActive = true;

    const fetchProfileAndRoles = async () => {
      try {
        const { data: roles } = await api.get<Role[]>('/users/roles');
        if (!isActive) return;
        setUser(buildUser(account, roles));
        setNeedsInteraction(false);
      } catch (error) {
        if (!isActive) return;
        const httpStatus = (error as any)?.response?.status;
        if (httpStatus === 401 || httpStatus === 403) {
          setStatus('unauthenticated');
        } else {
          setNeedsInteraction(true);
        }
      }
    };

    void fetchProfileAndRoles();

    return () => {
      isActive = false;
    };
  }, [accountId, inProgress, setUser, setStatus, clearUser, setNeedsInteraction]);
}
